import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageLoader from '../handout/handout-image-loader';
import UserApi from '../../../rest-api/user-api';
import { Link, useNavigate } from 'react-router-dom';
import BinArchiveIcon from '../../../assets/icons/BinArchive.svg';
import arrowRightIcon from '../../../assets/icons/Arrow-white_Right.svg';
import shareIcon from '../../../assets/icons/ShareHandoutFlight.svg';
import QuickShareDialog from '../handout/quick-share-dialog';
import { parseUserHandouts, stringifyUserHandouts } from '../../../utils/handouts-utils';

const ClearBundleBinIcon = ({ size }) => (
    <Box
        component="svg"
        viewBox="16 14 18 22.3869"
        aria-hidden="true"
        sx={{
            width: size,
            height: size,
            display: 'block',
            flexShrink: 0,
        }}
    >
        <path
            d="M32.5048 16.5357H30.3825V15.6284C30.3825 14.7281 29.6522 14 28.7518 14H21.0897C20.1917 14 19.4613 14.7304 19.4613 15.6284V16.5357H17.4975C16.6707 16.5357 16.0023 17.2063 16 18.0332V19.1701C16 19.3699 16.1631 19.5307 16.3606 19.5307H16.5168L17.5182 35.3533C17.5549 35.9321 18.035 36.3846 18.6161 36.3869H31.2829C31.8594 36.3823 32.3371 35.9367 32.3808 35.3602L33.5269 19.5284H33.6394C33.8392 19.5284 34 19.3676 34 19.1678V18.0309C34 17.204 33.3293 16.5334 32.5025 16.5334L32.5048 16.5357ZM20.1825 15.6284C20.1825 15.1277 20.589 14.7212 21.0897 14.7212H28.7518C29.2525 14.7212 29.6591 15.1277 29.6591 15.6284V16.5357H20.1825V15.6284ZM16.7212 18.0332C16.7212 17.606 17.0703 17.2569 17.4975 17.2569H32.5048C32.932 17.2569 33.2788 17.6037 33.2811 18.0332V18.8095H16.7212V18.0332ZM31.6619 35.3097C31.6504 35.5118 31.485 35.668 31.2852 35.6657H18.6161C18.4162 35.6657 18.2509 35.5095 18.2394 35.3097L17.2403 19.5307H32.8034L31.6596 35.3097H31.6619ZM20.5982 32.6982V22.1307C20.5982 21.9308 20.759 21.7701 20.9588 21.7701C21.1586 21.7701 21.3194 21.9308 21.3194 22.1307V32.6982C21.3194 32.898 21.1563 33.0588 20.9588 33.0588C20.7613 33.0588 20.5982 32.898 20.5982 32.6982ZM24.6658 33.1208V21.8642C24.6658 21.6644 24.8266 21.5036 25.0264 21.5036C25.2262 21.5036 25.387 21.6644 25.387 21.8642V33.1208C25.387 33.3207 25.2262 33.4814 25.0264 33.4814C24.8266 33.4814 24.6658 33.3207 24.6658 33.1208ZM28.7886 32.6982V22.1307C28.7886 21.9308 28.9493 21.7701 29.1492 21.7701C29.349 21.7701 29.5098 21.9308 29.5098 22.1307V32.6982C29.5098 32.898 29.349 33.0588 29.1492 33.0588C28.9493 33.0588 28.7886 32.898 28.7886 32.6982Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="0.8"
        />
    </Box>
);

function Handouts() {
    const { authStore, userDataStore, contentStore, imageStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [handouts, setHandouts] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const cardsPerView = 4;
    const canScrollLeft = currentIndex > 0;
    const canScrollRight = currentIndex + cardsPerView < handouts.length;
    const hasMoreThanCards = handouts.length > cardsPerView;

    useEffect(() => {
        // Safely parse handouts, default to empty array if undefined or not parseable
        const handoutsData = parseUserHandouts(userDataStore.userData.handouts);

        if (userDataStore.userData.handouts !== stringifyUserHandouts(handoutsData)) {
            userDataStore.updateUserHandouts(handoutsData);
        }

        const handoutIds = handoutsData.map((handout) => handout.contentId);

        const fetchedHandouts = contentStore.getContentFromIds(handoutIds).map((handout) => {
            const userDataHandout = handoutsData.find((data) => data.contentId === handout.contentId);
            if (userDataHandout) {
                return {
                    ...handout,
                    formattedCategories: userDataHandout.categories.split(',').map((category) => category.trim()),
                };
            }
            return handout;
        });
        setHandouts(fetchedHandouts);
    }, [userDataStore.userData.handouts, contentStore, userDataStore]);

    useEffect(() => {
        if (currentIndex > 0 && currentIndex >= handouts.length) {
            setCurrentIndex(Math.max(0, handouts.length - cardsPerView));
        }
    }, [currentIndex, handouts.length]);

    const handleDeleteHandout = async (handoutData) => {
        let isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser' ? true : false;

        const currentHandoutsData = parseUserHandouts(userDataStore.userData.handouts);
        const updatedHandoutsData = currentHandoutsData.filter(
            (handout) => handout.contentId !== handoutData.contentId,
        );

        let payload = {
            handouts: updatedHandoutsData,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            const handoutIds = updatedHandoutsData.map((handout) => handout.contentId);
            const fetchedHandouts = contentStore.getContentFromIds(handoutIds).map((handout) => {
                const userDataHandout = updatedHandoutsData.find((data) => data.contentId === handout.contentId);
                if (userDataHandout) {
                    return {
                        ...handout,
                        formattedCategories: userDataHandout.categories.split(',').map((category) => category.trim()),
                    };
                }
                return handout;
            });

            userDataStore.updateUserHandouts(updatedHandoutsData);
            setSnackbarMessage('Handout deleted from bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
            setHandouts(fetchedHandouts);
        } catch (error) {
            console.error('Error deleting handout from bundle: ', error);
            setSnackbarMessage('Failed to delete handout from bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleCleanBundle = async () => {
        let isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser' ? true : false;

        let payload = {
            handouts: [],
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            userDataStore.updateUserHandouts([]);
            setCurrentIndex(0);
            setHandouts([]);
            setSnackbarMessage('Handout bundle cleaned');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error cleaning handout bundle: ', error);
            setSnackbarMessage('Failed to clean handout bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleOpenHandout = (handoutData) => {
        localStorage.removeItem('loadSavedHandout');
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.removeItem('loadCustomisedHandoutInEditMode');
        localStorage.removeItem('openInCustomiseMode');
        navigate(`/handout/${handoutData.contentId}`);
    };

    const getSnackbarIcon = () => {
        return snackbarStatus === 'success' ? (
            <CheckIcon sx={{ ml: 1, color: 'white' }} />
        ) : (
            <CloseIcon sx={{ ml: 1, color: 'red' }} />
        );
    };

    return (
        <Box
            className="handouts-container"
            sx={{
                background: '#F0F0FC',
                maxWidth: '1440px',
                margin: '0 auto',
                width: '100%',
                px: { xs: '16px', md: '40px' },
                pt: { xs: '24px', md: '39px' },
                pb: { xs: handouts.length > 0 ? '120px' : '24px', md: handouts.length > 0 ? '32px' : '32px' },
                boxSizing: 'border-box',
                minHeight: 'calc(100vh - 83px)',
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '24px', md: '40px' },
                    lineHeight: { xs: '24px', md: '40px' },
                    letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                    color: '#000453',
                    mb: { xs: '22px', md: '28px' },
                }}
            >
                Handout bundle ({handouts.length})
            </Typography>

            {handouts.length === 0 ? (
                <Box
                    sx={{
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '20px', md: '28px' },
                    }}
                >
                    <Typography sx={{ color: '#000453' }}>
                        You haven’t added any handouts to your bundle yet!
                        <br />
                        <Link to="/search">Search for conditions here</Link>
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '20px 12px 16px', md: '21px 21px 15px' },
                        position: 'relative',
                        overflow: 'visible',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {!isMobile && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: '-46px',
                                    zIndex: 10,
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: hasMoreThanCards && canScrollLeft ? '#FF669B' : '#FFB3CE',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: hasMoreThanCards && canScrollLeft ? 'pointer' : 'default',
                                    ...(hasMoreThanCards && canScrollLeft && { '&:hover': { background: '#FF85B0' } }),
                                }}
                                onClick={() => canScrollLeft && setCurrentIndex((i) => i - 1)}
                            >
                                <img
                                    src={arrowRightIcon}
                                    alt="Previous"
                                    width={18}
                                    style={{ transform: 'scaleX(-1)', display: 'block' }}
                                />
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                gap: { xs: '20px', md: '21px' },
                                width: '100%',
                            }}
                        >
                            {(isMobile ? handouts : handouts.slice(currentIndex, currentIndex + cardsPerView)).map(
                                (handoutData) => {
                                    const locale = 'en-UK';
                                    const localeData = handoutData.contentData?.[locale] || [];
                                    const illustrationBlock = localeData
                                        .flatMap((section) => section.blocks)
                                        .find((block) => block.type === 'illustration');
                                    const imageSrc = handoutData.previewImage || illustrationBlock?.src || null;

                                    return (
                                        <Box
                                            key={handoutData.contentId}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleOpenHandout(handoutData)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleOpenHandout(handoutData);
                                                }
                                            }}
                                            sx={{
                                                background: '#F0F0FC',
                                                borderRadius: '20px',
                                                p: { xs: '14px', md: '21px' },
                                                minHeight: { xs: '411px', md: '414px' },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    background: '#FFFFFF',
                                                    borderRadius: '10px',
                                                    height: { xs: '254px', md: '322px' },
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <HandoutImageLoader
                                                    src={imageSrc}
                                                    alt={handoutData.contentName}
                                                    imageStore={imageStore}
                                                    handoutData={handoutData}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                sx={{
                                                    mt: '10px',
                                                    minHeight: '56px',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 600,
                                                    fontSize: { xs: '22px', md: '22px' },
                                                    lineHeight: { xs: '26px', md: '26px' },
                                                    letterSpacing: { xs: '-0.44px', md: '-0.44px' },
                                                    color: '#000453',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {handoutData.contentName}
                                            </Typography>

                                            <Box sx={{ mt: 'auto' }}>
                                                <Box
                                                    sx={{
                                                        height: '2px',
                                                        background: 'rgba(0, 4, 83, 0.10)',
                                                        mb: '8px',
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Box
                                                        component="button"
                                                        aria-label="delete"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleDeleteHandout(handoutData);
                                                        }}
                                                        onKeyDown={(event) => {
                                                            event.stopPropagation();
                                                        }}
                                                        sx={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            padding: 0,
                                                            display: 'flex',
                                                            cursor: 'pointer',
                                                            borderRadius: '50%',
                                                        }}
                                                    >
                                                        <img
                                                            src={BinArchiveIcon}
                                                            alt="Delete handout"
                                                            width={50}
                                                            height={50}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                },
                            )}
                        </Box>

                        {!isMobile && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '-46px',
                                    zIndex: 10,
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: hasMoreThanCards && canScrollRight ? '#FF669B' : '#FFB3CE',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: hasMoreThanCards && canScrollRight ? 'pointer' : 'default',
                                    ...(hasMoreThanCards && canScrollRight && { '&:hover': { background: '#FF85B0' } }),
                                }}
                                onClick={() => canScrollRight && setCurrentIndex((i) => i + 1)}
                            >
                                <img src={arrowRightIcon} alt="Next" width={18} style={{ display: 'block' }} />
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {handouts.length > 0 && (
                <Box
                    sx={{
                        position: isMobile ? 'fixed' : 'static',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto',
                        bottom: isMobile ? 0 : 'auto',
                        zIndex: isMobile ? 1200 : 'auto',
                        background: isMobile ? '#FFFFFF' : 'transparent',
                        borderTop: isMobile ? '1px solid #E5E5E5' : 'none',
                        height: isMobile ? '85px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        mt: isMobile ? 0 : '35px',
                        '@media print': { display: 'none' },
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: '1440px',
                            width: '100%',
                            px: { xs: '19px', md: 0 },
                            py: { xs: '18px', md: '12px' },
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: { xs: '10px', md: '16px' },
                        }}
                    >
                        <Box
                            component="button"
                            sx={{
                                borderRadius: { xs: '188px', md: '200px' },
                                background: '#FF669B',
                                color: '#fff',
                                minHeight: { xs: '47px', md: '50px' },
                                pl: { xs: '12px', md: '20px' },
                                pr: { xs: '7px', md: '10px' },
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: { xs: '12px', md: '18px' },
                                lineHeight: { xs: '14px', md: '18px' },
                                letterSpacing: { xs: '-0.28px', md: '-0.36px' },
                                textTransform: 'none',
                                boxShadow: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: { xs: '9.4px', md: '10px' },
                                '&:hover': { background: '#FF85B0', boxShadow: 'none' },
                            }}
                            onClick={() => setQuickShareOpen(true)}
                        >
                            <img
                                src={shareIcon}
                                alt=""
                                width={isMobile ? 17 : 22}
                                style={{ filter: 'brightness(0) invert(1)' }}
                            />
                            <span>Share {handouts.length} handout(s)</span>
                        </Box>
                        <Box
                            component="button"
                            sx={{
                                borderRadius: { xs: '188px', md: '200px' },
                                background: '#FF669B',
                                color: '#fff',
                                minHeight: { xs: '47px', md: '50px' },
                                px: { xs: '12px', md: '20px' },
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: { xs: '12px', md: '18px' },
                                lineHeight: { xs: '14px', md: '18px' },
                                letterSpacing: { xs: '-0.28px', md: '-0.36px' },
                                textTransform: 'none',
                                boxShadow: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: { xs: '7px', md: '10px' },
                                whiteSpace: 'nowrap',
                                '&:hover': { background: '#FF85B0', boxShadow: 'none' },
                            }}
                            onClick={handleCleanBundle}
                        >
                            <ClearBundleBinIcon size={isMobile ? 17 : 22} />
                            <span>Clear bundle</span>
                        </Box>
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {getSnackbarIcon()} {/* Dynamically selected icon based on the snackbar status */}
                    </Box>
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />

            {handouts.length > 0 && (
                <QuickShareDialog open={quickShareOpen} onClose={() => setQuickShareOpen(false)} handout={handouts} />
            )}
        </Box>
    );
}

export default Handouts;
